import type { ReactElement, ReactNode } from "react"
import type { UDSShadow } from "@gatsby-plugin-generic-page/fragments/udsShadow"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

const roundedClasses = {
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
}

const shadowClasses = {
  sm: "shadow-[0_39px_11px_0_rgba(0,0,0,0),0_25px_10px_0_rgba(0,0,0,0.01),0_14px_8px_0_rgba(0,0,0,0.03),0_6px_6px_0_rgba(0,0,0,0.06),0_2px_3px_0_rgba(0,0,0,0.07)]",
  md: "shadow-[0px_3px_7px_0px_rgba(0,0,0,0.07),0px_13px_13px_0px_rgba(0,0,0,0.06),0px_28px_17px_0px_rgba(0,0,0,0.04),0px_51px_20px_0px_rgba(0,0,0,0.01),0px_79px_22px_0px_rgba(0,0,0,0)]",
}

// Written as literal class names (not `shadow-${size}` interpolation) so
// Tailwind's static content scan can actually detect and generate them.
const UDS_SHADOW_CLASSES: Record<UDSShadow["size"], string> = {
  none: "",
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
  xl: "shadow-xl",
  "2xl": "shadow-2xl",
}

type SurfaceProps = {
  children: ReactNode
  className?: string
  roundedSize?: "sm" | "md" | "lg"
  bgColorClass?: string
  shadowSize?: "sm" | "md"
  /** uds-shadow component; overrides shadowSize's default, "none" removes the shadow. */
  shadow?: UDSShadow
  border?: boolean
}

export default function Surface({
  children,
  className,
  roundedSize = "sm",
  bgColorClass = "bg-surface-base",
  shadowSize = "md",
  shadow,
  border = false,
}: SurfaceProps): ReactElement<SurfaceProps> {
  const shadowClassName = !isEmpty(shadow)
    ? UDS_SHADOW_CLASSES[shadow.size]
    : shadowClasses[shadowSize]

  return (
    <div
      className={cn(
        roundedClasses[roundedSize],
        border ? "border border--base" : undefined,
        bgColorClass,
        shadowClassName,
        className
      )}
    >
      {children}
    </div>
  )
}
